<!--@format-->
<template>
    <div class="assign-user-contact-widget">
        <div class="widget-contents-container">
            <div class="widget-header">
                <h3>Assign a User</h3>
                <div class="svg-container widget-close-button" @click="closeWidget">
                    <widget-close-svg />
                </div>
            </div>
            <div class="content-wrapper">
                <div class="bordered-container">
                    <div class="title">Choose Users</div>
                    <div v-if="loading" class="loading-card">
                        <loading-screen-svg />
                    </div>
                    <div v-else class="input-container">
                        <agency-users-search
                            :options="getOptions()"
                            :selected="assignedUsers"
                            @assign="assignUser($event)"
                            @remove="removeUser($event)"
                        />
                    </div>
                </div>
            </div>
            <div class="widget-footer">
                <div class="cancel-container">
                    <app-button class="cancel-btn" @click="closeWidget"> Cancel </app-button>
                </div>
                <div class="action-container">
                    <app-button :disabled="isSaving" @click="save()">
                        {{ isSaving ? 'Saving...' : 'Save' }}
                    </app-button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
const AgencyUsersSearch = () => import('@/components/client/agency-users-search.vue');
const AppButton = () => import('@/components/common/app-button.vue');
const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg.vue');
const LoadingScreenSvg = () => import('@/components/svg/loading-screen-svg');

import EventBus from '@/scripts/event-bus.js';
import { differenceBy } from 'lodash';

export default {
    name: 'assign-user-contact-widget',

    components: {
        'agency-users-search': AgencyUsersSearch,
        'app-button': AppButton,
        'loading-screen-svg': LoadingScreenSvg,
        'widget-close-svg': WidgetCloseSvg,
    },

    props: {
        widget: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            loading: true,
            isSaving: false,
            agencyId: this.widget.agencyId,
            contactId: this.widget.contactId,
            availableUsers: [],
            assignedUsers: [],
        };
    },

    created() {
        this.getUsers();
        this.assignedUsers = [...this.widget.assignedUsers];
    },

    methods: {
        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },

        getOptions() {
            return differenceBy(this.availableUsers, this.assignedUsers, 'id');
        },

        save() {
            this.isSaving = true;
            const changes = this.assignedUsers.map((user) => {
                return user.id;
            });

            this.$api
                .put(
                    `/api/Vendors/${this.agencyId}/Contacts/${this.contactId}/AssignedUsers`,
                    changes
                )
                .then(() => {
                    this.$helpers.showSuccessNotification('Your changes have been saved.');
                    EventBus.$emit('contact-partner-lead:update');
                    EventBus.$emit('close-widget', this.widget);
                })
                .catch((error) => {
                    this.$helpers.showFailNotification(
                        'An error occurred while saving your information.',
                        { error },
                    );
                })
                .finally(() => {
                    this.isSaving = false;
                });
        },

        getUsers() {
            this.loading = true;
            const filter = `$filter=AgencyId eq ${this.agencyId}`;
            const query = `/service/Users?${filter}`;
            this.$api
                .get(query)
                .then((response) => {
                    this.availableUsers = response.data.value.map(this.mapAgencyUserToOption);
                })
                .catch((error) => {
                    this.$helpers.showFailNotification('An error occurred while fetching users.', { error });
                })
                .finally(() => {
                    this.loading = false;
                });
        },

        mapAgencyUserToOption(user) {
            return {
                id: user.Id,
                name: `${user.FirstName} ${user.LastName}`,
                profilePicture: user.ProfileImageUrl,
                title: user.Title,
            };
        },

        assignUser(userToAssign) {
            this.assignedUsers.push(userToAssign);
        },

        removeUser(userToRemove) {
            this.assignedUsers = this.assignedUsers.filter(({ id }) => id !== userToRemove.id);
        },
    },
};
</script>

<style>
.assign-user-contact-widget .form-group {
    margin-top: 1em;
}

.assign-user-contact-widget .app-input {
    margin-bottom: 1em;
}

.assign-user-contact-widget .bordered-container {
    border: 2px solid #e9e9ef;
    border-radius: 22px;
    margin: 25px 0;
    padding: 10px 20px;
    position: relative;
}

.assign-user-contact-widget .bordered-container .title {
    background-color: #ffffff;
    font-weight: 600;
    left: 50%;
    margin-left: -82px;
    padding: 0px 25px;
    position: absolute;
    top: -11px;
}

.assign-user-contact-widget .bordered-container .loading-card {
    text-align: center;
}

.assign-user-contact-widget .bordered-container .loading-card .loading-screen {
    margin-left: auto;
}

.assign-user-contact-widget .bordered-container .input-container {
    margin: 1.5em 0;
}

.assign-user-contact-widget .assigned-users {
    align-items: center;
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 8px;
}

.assign-user-contact-widget .assigned-users .user-chips {
    align-items: center;
    border: 1px solid #cac6cb;
    border-radius: 15px;
    display: flex;
    font-size: 12px;
    gap: 8px;
    padding: 3px 10px;
}

.assign-user-contact-widget .assigned-users .user-chips img {
    border-radius: 50%;
    height: 20px;
    width: 20px;
}

.assign-user-contact-widget .assigned-users .user-chips svg {
    cursor: pointer;
    margin-left: 12px;
    width: 10px;
}

.assign-user-contact-widget .cancel-btn {
    background: transparent;
    border: none;
    color: #877d88;
}

.assign-user-contact-widget .cancel-btn:hover {
    background: #e9e9ef;
}
</style>
